import { useMemo } from "react";
import { useGetActiveSubscriptions } from "#src/hooks";
import { SubscriptionFeature } from "../types";

export type QuotaMap = Record<string, SubscriptionFeature>;

export function useQuota() {
  const {
    subscriptions,
    isLoading,
    error,
    refetch: refetchActiveSubscriptions,
  } = useGetActiveSubscriptions();

  const quotas: QuotaMap | undefined = useMemo(() => {
    return subscriptions?.features;
  }, [subscriptions?.features]);

  const helpers = useMemo(
    () => ({
      canUse: (feature: string): boolean => {
        const quota = quotas?.[feature];
        if (!quota) return true;
        if (quota.quota_limit === null) return true;
        return quota.usage < quota.quota_limit;
      },

      remaining: (feature: string): number => {
        const quota = quotas?.[feature];
        if (quota?.quota_limit === undefined || quota?.quota_limit === null)
          return Infinity;
        return Math.max(0, quota.quota_limit - quota.usage);
      },

      getQuota: (feature: string): SubscriptionFeature | undefined => {
        return quotas?.[feature];
      },

      isNearLimit: (feature: string, threshold = 0.8): boolean => {
        const quota = quotas?.[feature];
        if (quota?.quota_limit === undefined || quota?.quota_limit === null)
          return false;
        return quota.usage / quota.quota_limit >= threshold;
      },

      isBlocked: (feature: string): boolean => {
        const quota = quotas?.[feature];
        if (!quota) return false;
        if (quota.quota_limit === null) return false;
        const overLimit = quota.usage >= quota.quota_limit;
        return overLimit && quota.over_quota_policy === "Block";
      },
    }),
    [quotas]
  );

  const refetch = () => {
    refetchActiveSubscriptions().catch((error) => {
      console.error("Error refetching active subscriptions", error);
    });
  };
  const refetchAsync = refetchActiveSubscriptions;

  return {
    quotas,
    isLoading,
    error,
    refetch,
    refetchAsync,
    ...helpers,
  };
}
